import { useMemo } from 'react';
import styled from '@emotion/styled';
import type { IconName } from '@hotjar/hotsauce';
import { Button, Divider, Icon, Link, Modal, Stack, Text, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { closedModal, loadedModal, trackClick } from '@hotjar/webapp/tracking/common';
import { extractAccountGroupingProps } from '@hotjar/webapp/tracking/utils';
import type { OwnedAccount } from '@hotjar/account';
import { useOwnedAccount, mapOwnedAccountModelToDto } from '@hotjar/account';

import { usePricingTableByAccount } from '@hotjar/webapp/hooks/usePricingTable';
import { usePlanRepository } from '@hotjar/webapp/hooks/usePlanRepository';
import { BASIC } from '@hotjar/webapp/domains/revenue/plan/feature-group';
import { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { useReadableProductName } from '@hotjar/webapp/domains/common/product/hooks/useReadableProductName';
import { css } from '@emotion/react';
import { useEffectOnceWithDependencies } from '@hotjar/webapp/hooks/useEffectOnceWithDependencies';
import { MODAL_NAME } from '../types';

export type HighestPlans = Record<ProductType, FeatureGroup>;
interface Props {
  onSubmit(): void;
  onCancel(): void;
  highestPlans: HighestPlans;
}

type ExtendedProductType = ProductType | 'workspaces';

const S = {
  Modal: styled(Modal)`
    width: fit-content;
  `,
  ModalBody: styled(Modal.Body)`
    ${tokens.flexContainer('default', { gap: tokens.spacer.x3 })}
    flex: 1;
    padding-top: ${tokens.spacer.x8};
  `,
  TextNoMargin: styled(Text)`
    margin: 0;
  `,
  Content: styled(Stack)`
    flex: 1;
  `,
  ProductsContainer: styled.div`
    display: flex;
    & > * {
      flex: 1;
    }
  `,
  ProductAndProductIconContainer: styled.div`
    ${tokens.flexContainer('default', { gap: tokens.spacer.x1 })}
  `,
  ProductFeaturesContainer: styled.div`
    ${tokens.flexContainer('default', { gap: tokens.spacer.x1, direction: 'column' })}
  `,
  FeaturesList: styled.ul`
    ${tokens.flexContainer('form', { gap: tokens.spacer.x1 })}
    list-style: none;
    margin: 0;
    padding: 0;
  `,
  FeatureItem: styled.li`
    ${tokens.flexContainer('default', { gap: tokens.spacer.x1 })}
    align-items: center;
  `,
  Divider: styled(Divider)`
    margin-top: auto;
  `,
  CtaContainer: styled(Stack)`
    justify-content: space-between;
    gap: ${tokens.spacer.x3};
  `,
  Stack: styled(Stack)`
    background-color: ${tokens.color.bg_info};
    padding: ${tokens.spacer.x2};
  `,
  Icon: styled(Icon)<{ iconColor: string }>`
    min-width: 21px;
    min-height: 24px;
    ${({ iconColor }) =>
      css`
        color: ${iconColor}}
      `}
  `,
};

const actions = [
  { key: 'compare-plans', url: 'https://www.hotjar.com/pricing/' },
  { key: 'get-started', url: 'https://www.hotjar.com/customers/' },
];

export function MultiSkuChurnDeflectionModal({
  onSubmit,
  onCancel,
  highestPlans,
}: Props): JSX.Element {
  const { t } = useTranslationWithPrefix('billing:multi-sku-churn-deflection');

  const { data: account } = useOwnedAccount();
  const { priceOrderedPlans } = usePricingTableByAccount(
    mapOwnedAccountModelToDto(account as OwnedAccount),
  );
  const planRepository = usePlanRepository();

  useEffectOnceWithDependencies(
    () => {
      loadedModal(MODAL_NAME.multiSkuChurnDeflection, extractAccountGroupingProps(account));
    },
    [account],
    () => account !== undefined,
  );

  const defaultBasicPlan = useMemo(
    () =>
      priceOrderedPlans
        .map(([planId]) => planRepository.byId(planId))
        .find((plan) => plan.featureGroup === BASIC),
    [planRepository, priceOrderedPlans],
  );

  const productsTitlesAndIcons: Record<
    ExtendedProductType,
    { icon: { name: IconName; color: string }; title: string }
  > = {
    [ProductType.Analytics]: {
      icon: {
        name: 'hidden',
        color: tokens.BRAND_COLORS.peach_400,
      },
      title: useReadableProductName(ProductType.Analytics),
    },
    [ProductType.Feedback]: {
      icon: {
        name: 'question',
        color: '#8363DF',
      },
      title: useReadableProductName(ProductType.Feedback),
    },
    workspaces: {
      icon: {
        name: 'grid_1',
        color: tokens.BRAND_COLORS.cobalt_800,
      },
      title: t('products.workspaces.title'),
    },
  };

  function handleCancel() {
    closedModal(MODAL_NAME.multiSkuChurnDeflection, extractAccountGroupingProps(account));
    onCancel();
  }

  function handleSubmit() {
    trackClick({
      name: 'Continue to downgrade',
      uiPosition: `${MODAL_NAME.multiSkuChurnDeflection} modal`,
      groupData: extractAccountGroupingProps(account),
    });
    onSubmit();
  }

  function shouldShowProduct(product: ExtendedProductType, plan: FeatureGroup) {
    if (product === 'workspaces') {
      return Object.values(highestPlans).some((currentPlan) => currentPlan === 'business');
    }
    return plan !== 'basic';
  }

  const entries = Object.entries({
    ...highestPlans,
    workspaces: '',
  }) as [ExtendedProductType, FeatureGroup][];

  return (
    <S.Modal isOpen contentLabel={t('title')} onClose={handleCancel}>
      <S.ModalBody>
        <S.Content direction="column" spacing="x3">
          <Text as="h2" textType="header4">
            {t('title')}
          </Text>

          <S.ProductsContainer>
            {entries.map(
              ([product, plan]) =>
                shouldShowProduct(product, plan) && (
                  <S.ProductAndProductIconContainer key={product}>
                    <S.Icon
                      icon={productsTitlesAndIcons[product]?.icon.name}
                      iconColor={productsTitlesAndIcons[product]?.icon.color}
                    />
                    <S.ProductFeaturesContainer>
                      <Text as="h5" textType="header5">
                        {productsTitlesAndIcons[product]?.title}
                      </Text>
                      <Text textType="subtitle2">{t(`products.${product}.subtitle`)}</Text>

                      {product === 'workspaces' ? (
                        <Text as="span" textType="body2" textColor="text_light">
                          {t('products.workspaces.description')}
                        </Text>
                      ) : (
                        <S.FeaturesList>
                          {t<string, string[]>(`products.${product}.features.${plan}`, {
                            returnObjects: true,
                            ccDailyAllowance: defaultBasicPlan?.ccDailyAllowance,
                          }).map?.((text) => (
                            <S.FeatureItem key={text}>
                              <Icon icon="close" color="text_warning" />
                              <Text as="span" textType="body2" textColor="text_light">
                                {text}
                              </Text>
                            </S.FeatureItem>
                          ))}
                        </S.FeaturesList>
                      )}
                    </S.ProductFeaturesContainer>
                  </S.ProductAndProductIconContainer>
                ),
            )}
          </S.ProductsContainer>

          <S.Divider />

          <S.CtaContainer>
            {actions.map(({ key, url }) => (
              <S.Stack direction="column" spacing="x2" key={key}>
                <S.TextNoMargin as="p" textType="subtitle2">
                  {t(`${key}.title`)}
                </S.TextNoMargin>
                <S.TextNoMargin as="p" textType="body2" textColor="text_light">
                  {t(`${key}.description`)}
                </S.TextNoMargin>
                <Link
                  href={url}
                  onClick={() => {
                    trackClick({
                      name: t(`${key}.action`),
                      uiPosition: MODAL_NAME.multiSkuChurnDeflection,
                      groupData: extractAccountGroupingProps(account),
                    });
                  }}
                  kind="standalone"
                  isExternal
                  title={t(`${key}.title`)}
                >
                  {t(`${key}.action`)}
                </Link>
              </S.Stack>
            ))}
          </S.CtaContainer>
        </S.Content>
      </S.ModalBody>
      <Modal.Footer>
        <Button kind="ghost" onClick={handleSubmit}>
          {t('action.continue')}
        </Button>
        <Button onClick={handleCancel}>{t('action.cancel')}</Button>
      </Modal.Footer>
    </S.Modal>
  );
}
